import React from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {guardTourPress} from 'firstVisit/events';

import {Sparkles, X} from 'components/Icons';

import { TYPE, FONTS, TAG } from 'style/typography';

/**
 * "Session ended · Start again" (owner 2026-09-29): a session that simply ended is not a limit, so
 * it is one quiet line where the tutor was - no popup, no upgrade.
 */
export function SessionEndedLine({bottom, idle = false, onStart, onDismiss}) {
  return (
    <View style={[styles.anchor, {bottom}]} pointerEvents="box-none">
      <View style={styles.quiet}>
        {/* after a quiet minute the tutor closes to save the learner's minutes (Sol tutor-idle C) */}
        <Text style={styles.quietText}>{idle ? 'Tutor paused to save your minutes' : 'Session Ended'}</Text>
        <Text style={styles.divider}>·</Text>
        <TouchableOpacity onPress={onStart} accessibilityRole="button">
          <Text style={styles.action}>Start Again</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onDismiss} accessibilityLabel="Dismiss" style={styles.dismiss}>
          <X size={14} color="rgba(246,251,255,0.5)" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

/** "2 min left | Upgrade" (or "Buy more" on the top plan) - the approved low-minutes pill. */
export function LowMinutesPill({label, action, onPress}) {
  return (
    <View style={[styles.quiet, styles.low]}>
      <Text style={styles.lowText}>{label}</Text>
      <Text style={styles.divider}>|</Text>
      <TouchableOpacity onPress={onPress} accessibilityRole="button">
        <Text style={styles.action}>{action}</Text>
      </TouchableOpacity>
    </View>
  );
}

/**
 * The top-bar plan chip for Free learners (owner 2026-09-29): "Your plan: Free · LIMITED" with the Upgrade
 * CTA beside it, in Quikbase and beside Start Lesson in a unit. Upgrade opens the full-screen plans.
 * `compact` (narrow HUD layouts): only the Upgrade button, with the plan still named for screen readers.
 */
export function UpgradePill({height, radius, onPress, style, compact = false}) {
  if (compact) {
    return (
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel="Your plan: Free, limited. Upgrade"
        testID="hud-upgrade"
        onPress={guardTourPress('hud-upgrade', onPress)}
        style={[styles.chipCompact, {height, borderRadius: radius}, style]}>
        <Sparkles size={16} color="#10151F" />
        <Text style={styles.upgradeText}>Upgrade</Text>
      </TouchableOpacity>
    );
  }
  return (
    <View style={[styles.chip, {height, borderRadius: radius}, style]}>
      <Text style={styles.chipLabel}>Your plan:</Text>
      <Text style={styles.chipPlan}>Free</Text>
      <Text style={styles.chipDot}>·</Text>
      <View style={styles.limited}>
        <Text style={styles.limitedText}>LIMITED</Text>
      </View>
      <View style={styles.chipDivider} />
      <TouchableOpacity testID="hud-upgrade" accessibilityRole="button" accessibilityLabel="Upgrade" onPress={guardTourPress('hud-upgrade', onPress)} style={styles.chipAction}>
        <Sparkles size={16} color="#10151F" />
        <Text style={styles.upgradeText}>Upgrade</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  anchor: {position: 'absolute', left: 0, right: 0, alignItems: 'center', zIndex: 100},
  quiet: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: 'rgba(16,21,31,0.78)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  quietText: {
    color: 'rgba(246,251,255,0.72)',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '600',
  },
  divider: {
    marginHorizontal: 8,
    color: 'rgba(255,255,255,0.3)',
    fontFamily: FONTS.body,
  },
  action: {
    color: '#C5E836',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
  dismiss: {marginLeft: 10, padding: 2},
  low: {borderColor: 'rgba(252,211,77,0.4)', backgroundColor: 'rgba(0,0,0,0.7)', paddingVertical: 4},
  lowText: {
    color: '#FEF3C7',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    fontWeight: '600',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: 4,
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.45)',
    backgroundColor: 'rgba(15,16,20,0.86)',
  },
  chipLabel: {
    color: 'rgba(246,251,255,0.62)',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '600',
  },
  chipPlan: {
    marginLeft: 5,
    color: '#F6FBFF',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
  chipDot: {
    marginLeft: 6,
    color: 'rgba(246,251,255,0.45)',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
  limited: {marginLeft: 6, paddingVertical: 2, paddingHorizontal: 6, borderRadius: 999, backgroundColor: 'rgba(252,211,77,0.15)'},
  // the shared caps tag style (Sol mobile-type-audit D1 B: 14/600, letter-spacing 1)
  limitedText: {
    color: '#FDE68A',
    fontFamily: FONTS.body,
    ...TAG,
  },
  chipDivider: {width: 1, height: '55%', marginHorizontal: 10, backgroundColor: 'rgba(255,255,255,0.14)'},
  chipAction: {flexDirection: 'row', alignItems: 'center', height: '80%', paddingHorizontal: 12, borderRadius: 8, backgroundColor: '#C5E836'},
  chipCompact: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, backgroundColor: '#C5E836'},
  upgradeText: {
    marginLeft: 6,
    color: '#10151F',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
});
